import React, { useCallback, useState } from 'react'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import { PackageOptionView } from 'selectors/cart/cartSelectors'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import InclusionsV2Section from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Divider from 'components/Luxkit/Divider'
import { pluralizeToString } from 'lib/string/pluralize'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SlideDown from 'components/Common/SlideDown'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import LePackageUpgradePrice from './LePackageUpgradePrice'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import LePackageUpgradeWithVelocityPoints from './LePackageUpgradeWithVelocityPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { rem } from 'polished'
import LuxuryPlusInclusions from 'loyalty/components/LuxuryPlusInclusions'
import { isActiveLuxuryPlusMember } from 'loyalty/selectors/featureToggle'

const StyledSlideDown = styled(SlideDown)`
  &.closed {
    margin-top: 0;
  }
`

const ImageWrapper = styled.div`
  display: flex;
  align-items: end;
`

const imageProps: ImageParams = {
  mobileAspectRatio: '11:5',
  tabletAspectRatio: '22:15',
  tabletWidth: '420px',
  quality: 'good',
}

const imagePropsWhenVelocityPointsIsPresent: ImageParams = {
  ...imageProps,
  tabletWidth: rem(200),
}

interface Props {
  packageUpgrade: PackageOptionView;
  selectedPackage?: OfferPackageOptionView;
  hasFlightsSelected?: boolean;
}

function LePackageUpgradeCard({
  packageUpgrade,
  selectedPackage,
  hasFlightsSelected,
}: Props) {
  const partnership = useAppSelector((state: App.State) => state.geo.partnership?.code)
  const isLuxplusMember = useAppSelector((state: App.State) => isActiveLuxuryPlusMember(state))

  const isVelocityPartnership = partnership === VIRGIN_VELOCITY_PARTNERSHIP_CODE

  const offer = packageUpgrade.offer
  const name = packageUpgrade.package.name
  const roomType = packageUpgrade.package.roomType
  const firstRoomImageId = packageUpgrade.package.roomType?.images?.[0]?.id

  const { inclusions, bonusInclusions, loyaltyInclusions } = useOfferInclusions(packageUpgrade.package)

  const hasInclusionsV1 = packageUpgrade.package.inclusions && packageUpgrade.package.inclusions.length > 0
  const showInclusionV1 = hasInclusionsV1 && (inclusions.length > 0 || bonusInclusions.length > 0)
  const isDifferentRoom = packageUpgrade.package.roomType?.id !== selectedPackage?.package.roomType?.id
  const hasLoyaltyInclusions = loyaltyInclusions.length > 0

  const selectedPackageInclusionsCountV2 = selectedPackage?.package.inclusionsV2?.length || 0
  const packageUpgradeInclusionsCountV2 = packageUpgrade.package.inclusionsV2?.length || 0
  const hasMoreInclusionsV2 = packageUpgradeInclusionsCountV2 > selectedPackageInclusionsCountV2

  const [showDetails, setShowDetails] = useState(false)
  const onViewDetails = useCallback(() => {
    setShowDetails(!showDetails)
  }, [showDetails])

  return <Group direction="vertical" gap={12}>
    <Group direction="vertical" tabletDirection="horizontal" gap={12} fullWidth>
      <Group direction="vertical" gap={12} fullWidth>
        {(isDifferentRoom || hasMoreInclusionsV2) && (
          <LabelGroup>
            {isDifferentRoom && <Label kind="tertiary" variant="highlight">Upgraded room</Label>}
            {hasMoreInclusionsV2 && <Label kind="tertiary" variant="highlight">More inclusions</Label>}
          </LabelGroup>
        )}
        <div>
          <Heading variant="heading6" colour="neutral-one">{name}</Heading>
          <Caption colour="neutral-three" variant="medium">{roomType?.name}</Caption>
        </div>
        {roomType?.sizeSqm && (
          <CSSBreakpoint min="tablet">
            <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>
          </CSSBreakpoint>
        )}
        <CSSBreakpoint min="tablet">
          {isVelocityPartnership && <LePackageUpgradeWithVelocityPoints packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlightsSelected}/>}
          {!isVelocityPartnership && <LePackageUpgradePrice packageUpgrade={packageUpgrade}hasFlightsSelected={hasFlightsSelected} />}
        </CSSBreakpoint>
      </Group>

      <CSSBreakpoint max="mobile">
        <Divider kind="primary" />
      </CSSBreakpoint>

      {firstRoomImageId && (
        <ImageWrapper>
          <ResponsiveImage
            {...(isVelocityPartnership ? imagePropsWhenVelocityPointsIsPresent : imageProps)}
            id={firstRoomImageId}
            alt={`${roomType?.name} image 1 at ${offer.property?.name} by ${offer.property?.geoData.administrativeAreaLevel2}, ${offer.property?.geoData.administrativeAreaLevel1}, ${offer.property?.geoData.country}`}
          />
        </ImageWrapper>
      )}

      <CSSBreakpoint max="mobile">
        {isVelocityPartnership && <LePackageUpgradeWithVelocityPoints packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlightsSelected}/>}
        {!isVelocityPartnership && <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlightsSelected} />}
      </CSSBreakpoint>

      {roomType?.sizeSqm && (
        <CSSBreakpoint max="mobile">
          <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>
        </CSSBreakpoint>
      )}
    </Group>

    <CSSBreakpoint min="tablet">
      <Divider kind="primary" />
    </CSSBreakpoint>

    <VerticalSpacer gap={16}>
      {isLuxplusMember && hasLoyaltyInclusions && <LuxuryPlusInclusions loyaltyInclusions={loyaltyInclusions} accommodationOffer={offer}/>}
      {showInclusionV1 && (
        <Group direction="vertical" gap={12}>
          {(inclusions.length > 0 && bonusInclusions.length === 0) && <BodyText variant="small" weight="bold">Inclusions:</BodyText>}
          <InclusionsSection
            inclusions={inclusions}
            bonusInclusions={bonusInclusions}
            size="small"
          />
        </Group>
      )}
      {!hasInclusionsV1 && packageUpgrade.package.inclusionsV2 && (
        <Group direction="vertical" gap={12}>
          <BodyText variant="small" weight="bold">{pluralizeToString('inclusion', packageUpgrade.package.inclusionsV2.length)}:</BodyText>
          <InclusionsV2Section inclusions={packageUpgrade.package.inclusionsV2} size="small" />
        </Group>
      )}
      <StyledSlideDown show={showDetails} animateOpacity className={showDetails ? '' : 'closed'}>
        <LePackageUpgradeCardRoomDetails roomType={roomType} />
      </StyledSlideDown>
      <SmallToggleButton size="caption" on={showDetails} onClick={onViewDetails}>
        {showDetails ? 'Hide' : 'View'} more details
      </SmallToggleButton>
      {!isLuxplusMember && hasLoyaltyInclusions && <LuxuryPlusInclusions loyaltyInclusions={loyaltyInclusions} accommodationOffer={offer}/>}
    </VerticalSpacer>
  </Group>
}

export default LePackageUpgradeCard
